import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { trackEsimEvent } from './esimAnalytics';
import { LAST_UPDATED, PHONE_SOURCES, matchPhones, type PhoneMatch } from './esimPhones';
import './CompatibilityDialog.css';

export type CompatTab = 'check' | 'phones';

interface Props {
  open: boolean;
  tab: CompatTab;
  onClose: () => void;
  onTab: (tab: CompatTab) => void;
}

const DIAL = '*#06#';
const OS = {
  ios: {
    path: ['Settings', 'Mobile Service (or Cellular)', 'Add eSIM'],
    hint: 'If you see “Add eSIM”, your iPhone supports it.',
  },
  android: {
    path: ['Settings', 'Connections (or Network & internet)', 'SIM manager (or SIMs)', 'Add eSIM'],
    hint: 'Menu names vary by brand. Look for an option to add or download a SIM.',
  },
};

async function copyDialCode(): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(DIAL);
    return true;
  } catch {
    const area = document.createElement('textarea');
    area.value = DIAL;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch { ok = false; }
    area.remove();
    return ok;
  }
}

export const CompatibilityDialog: React.FC<Props> = ({ open, tab, onClose, onTab }) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [os, setOs] = useState<'ios' | 'android'>('ios');
  const [copied, setCopied] = useState(false);
  const [query, setQuery] = useState('');
  const [live, setLive] = useState('');
  const path = OS[os];
  const matches = matchPhones(query);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      previous?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>('button, a[href], input, summary')]
        .filter((node) => !node.closest('[hidden]') && !node.hasAttribute('disabled'));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const selectTab = (next: CompatTab, reason: 'click' | 'arrow' | 'from-check' = 'click') => {
    onTab(next);
    trackEsimEvent({ name: 'compat_tab', tab: next });
    if (reason === 'from-check') trackEsimEvent({ name: 'compat_goto_phones_from_check' });
    window.setTimeout(() => {
      if (reason === 'from-check' && next === 'phones') searchRef.current?.focus();
      else if (reason === 'arrow') document.getElementById(next === 'check' ? 'compat-tab-check' : 'compat-tab-phones')?.focus();
    }, 0);
  };

  const onSearch = (value: string) => {
    setQuery(value);
    const found = matchPhones(value);
    if (normalizeHasQuery(value)) {
      const text = `${found.total} ${found.total === 1 ? 'model' : 'models'} found`;
      setLive(text);
      trackEsimEvent({ name: 'compat_search', hasResults: found.total > 0 });
    }
  };

  const copy = async () => {
    if (!(await copyDialCode())) return;
    setCopied(true);
    setLive('Copied');
    trackEsimEvent({ name: 'compat_dial_copied' });
    window.setTimeout(() => setCopied(false), 1800);
  };

  if (!open) return null;

  return createPortal(
    <div
      className="cwy-esim-theme esim-compat-scrim"
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <section
        className="esim-compat"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={dialogRef}
      >
        <div className="esim-compat-live" role="status" aria-live="polite">{live}</div>
        <header className="esim-compat-head">
          <div className="esim-compat-top">
            <div>
              <p className="esim-eyebrow">Compatibility</p>
              <h2 id={titleId}>Is your phone eSIM-ready?</h2>
              <p>Two ways to find out: a 10-second dial code, or look up your model.</p>
            </div>
            <button type="button" className="esim-x" aria-label="Close" onClick={onClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>
          <div className="esim-compat-tabs" role="tablist" aria-label="Compatibility options" onKeyDown={(event) => {
            if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
            event.preventDefault();
            selectTab(tab === 'check' ? 'phones' : 'check', 'arrow');
          }}>
            <button type="button" role="tab" id="compat-tab-check" aria-controls="compat-panel-check" aria-selected={tab === 'check'} tabIndex={tab === 'check' ? 0 : -1} onClick={() => selectTab('check')}>Quick check</button>
            <button type="button" role="tab" id="compat-tab-phones" aria-controls="compat-panel-phones" aria-selected={tab === 'phones'} tabIndex={tab === 'phones' ? 0 : -1} onClick={() => selectTab('phones')}>Supported phones</button>
          </div>
        </header>
        <div className="esim-compat-body">
          <div role="tabpanel" id="compat-panel-check" aria-labelledby="compat-tab-check" hidden={tab !== 'check'}>
            <section className="esim-compat-step">
              <div className="esim-compat-step-h"><span className="esim-compat-num">1</span><h3>Dial this on your phone</h3></div>
              <p>Open your <b>Phone app</b>, as if you were making a call, and type:</p>
              <div className="esim-compat-dial">
                <span className="esim-compat-code" aria-label="star hash zero six hash">{DIAL}</span>
                <div className="esim-compat-acts">
                  <button type="button" className={copied ? 'esim-btn esim-btn-ghost esim-compat-sm is-done' : 'esim-btn esim-btn-ghost esim-compat-sm'} onClick={() => { void copy(); }}>
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                  <a className="esim-btn esim-compat-sm esim-compat-touch" href="tel:*%2306%23" onClick={() => trackEsimEvent({ name: 'compat_dialer_clicked' })}>Open dialer</a>
                </div>
              </div>
              <p className="esim-compat-hint">No need to press call. A screen with your phone’s IDs appears by itself.</p>
            </section>
            <section className="esim-compat-step">
              <div className="esim-compat-step-h"><span className="esim-compat-num">2</span><h3>Look for “EID”</h3></div>
              <div className="esim-compat-see">
                <div className="esim-compat-phone" aria-hidden="true">
                  <div className="esim-compat-phone-t">What you’ll see</div>
                  <div className="esim-compat-row"><span>IMEI</span>35 •••••• •••••• 7</div>
                  <div className="esim-compat-row is-hi"><span>EID</span>8904 •••• •••• •••• •••• •••• ••12</div>
                </div>
                <div className="esim-compat-outs">
                  <p className="esim-compat-out is-yes"><b>You see an EID</b>Your phone supports eSIM.</p>
                  <p className="esim-compat-out is-maybe"><b>No EID shown</b>Not conclusive: some eSIM phones don’t display it. Try step 3 or the supported list.</p>
                </div>
              </div>
            </section>
            <section className="esim-compat-step">
              <div className="esim-compat-step-h"><span className="esim-compat-num">3</span><h3>Or check in Settings</h3><span className="esim-compat-opt">Optional</span></div>
              <div className="esim-compat-seg" role="group" aria-label="Phone type">
                <button type="button" aria-pressed={os === 'ios'} onClick={() => setOs('ios')}>iPhone</button>
                <button type="button" aria-pressed={os === 'android'} onClick={() => setOs('android')}>Android</button>
              </div>
              <p className="esim-compat-path">
                {path.path.map((part, index) => (
                  <React.Fragment key={part}>
                    {index > 0 ? <span aria-hidden="true">›</span> : null}
                    <span className={index === path.path.length - 1 ? 'is-end' : undefined}>{part}</span>
                  </React.Fragment>
                ))}
              </p>
              <p className="esim-compat-hint">{path.hint}</p>
            </section>
            <section className="esim-compat-step">
              <div className="esim-compat-step-h"><span className="esim-compat-num">4</span><h3>Make sure it’s unlocked</h3></div>
              <p>A carrier-locked phone may install the eSIM but fail to activate it. <b>iPhone:</b> Settings › General › About › <b>Carrier Lock</b> should say “No SIM restrictions”. <b>Android:</b> ask your carrier if the phone is unlocked.</p>
            </section>
            <div className="esim-compat-cta">
              <span>Phone not showing an EID?</span>
              <button type="button" className="esim-btn" onClick={() => selectTab('phones', 'from-check')}>See supported phones</button>
            </div>
          </div>
          <div role="tabpanel" id="compat-panel-phones" aria-labelledby="compat-tab-phones" hidden={tab !== 'phones'}>
            <label className="esim-compat-search">
              <span className="esim-compat-sr">Search your phone model</span>
              <input
                ref={searchRef}
                type="search"
                value={query}
                autoComplete="off"
                placeholder="Search your model, e.g. iPhone 14 or Galaxy S24"
                onChange={(event) => onSearch(event.target.value)}
              />
            </label>
            {matches.brands.length === 0 ? (
              <div className="esim-compat-empty">
                <b>No match for “{query}”</b>
                <p>That doesn’t mean it’s unsupported: not every model is listed. Dial *#06# to check your exact phone.</p>
                <button type="button" className="esim-btn" onClick={() => selectTab('check')}>Use the dial code</button>
              </div>
            ) : (
              matches.brands.map((item, index) => (
                <BrandBlock key={item.brand.id} item={item} open={Boolean(query) || index === 0} />
              ))
            )}
            <p className="esim-compat-src">
              Last updated {LAST_UPDATED}. Lists aren’t exhaustive and can vary by region. *#06# is the final check. Official lists:{' '}
              {PHONE_SOURCES.map(([label, href], index) => (
                <React.Fragment key={href}>
                  {index > 0 ? ' · ' : null}
                  <a href={href} target="_blank" rel="noopener noreferrer">{label}</a>
                </React.Fragment>
              ))}
              .
            </p>
          </div>
        </div>
      </section>
    </div>,
    document.body,
  );
};

function BrandBlock({ item, open }: { item: PhoneMatch; open: boolean }) {
  return (
    <details className="esim-compat-brand" open={open}>
      <summary>
        {item.brand.name}
        <span>{item.count} models</span>
      </summary>
      <div className="esim-compat-brand-in">
        <p>{item.brand.note}</p>
        {item.groups.map(([series, models]) => (
          <div className="esim-compat-series" key={series}>
            <h4>{series}</h4>
            <div>{models.map((model) => <span className="esim-compat-chip" key={model}>{model}</span>)}</div>
          </div>
        ))}
      </div>
    </details>
  );
}

function normalizeHasQuery(value: string): boolean {
  return value.trim().length > 0;
}
